import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { changeStatusAppointmentThunk } from '@spike/bookings-action';
import clsx from 'clsx';
import ListPetInfo from 'components/Bookings2/UI/ListPetInfo/ListPetInfo';
import StatusButton from 'components/OrderDetail/StatusButton';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { usePermissions, useTimeZone } from 'hooks';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import styles from './EvaluationTableRow.module.scss';
import NotesTableRow from './NotesTableRow';
import PetParent from './PetParent';
import Service from './Service';
import PaidIcon from './PaidIcon';
import DepositPendingIcon from './DepositPendingIcon';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { useApiClientWrapper } from 'hooks';
import { dispatch } from 'store';
import { EvaluationReportDrawer } from 'components/UI/EvaluationReportDrawer/EvaluationReportDrawer';

interface EvaluationTableRowProps {
    app: AppointmentListItem;
    isLast?: boolean;
    scrollerContainerId: string;
    className?: string;
    onCheckout?: () => void;
    onShowOrder?: () => void;
    onShowClient?: () => void;
    onShowPet?: () => void;
}

export const EvaluationTableRow: FunctionComponent<EvaluationTableRowProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const timezone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();
    const [pendingEvaluationStatusId, setPendingEvaluationStatusId] = useState<number>();

    const permissions = usePermissions();
    const isCheckoutAllowed = permissions.checkout.takePayments;

    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const allowActions = isUserLoggedInMarketplace(props.app.marketplaceId);

    const hasNotes = props.app.notes.trim().length > 0;

    const checkoutOption = {
        id: 'checkout',
        label: 'Checkout',
        icon: faMoneyBill,
        onClick: () => props.onCheckout?.()
    };

    const options = isCheckoutAllowed ? [checkoutOption] : [];

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        props.onShowOrder?.();
    };

    const date = props.app.startAt.tz(timezone);

    const petInfo = (
        <ListPetInfo
            orderId={props.app.orderId}
            pet={props.app.pet}
            onClick={allowActions ? props.onShowPet : undefined}
        />
    );

    const service = (
        <Service
            service={props.app.service}
            petId={props.app.pet.id}
            marketplaceId={props.app.marketplaceId}
        />
    );

    const statusButton = (
        <StatusButton
            appointmentId={props.app.appointmentId}
            apppointmentMarketplaceId={props.app.marketplaceId}
            appointmentStatus={props.app.status}
            appointmentClosedAt={props.app.closedAt ?? undefined}
            appointmentCancelationReasonId={props.app.cancellationReasonId ?? null}
            recurringUuid={props.app.recurringUuid ?? undefined}
            customerFirstName={props.app.customer.firstName}
            customerLastName={props.app.customer.lastName}
            businessAreaId={props.app.service.businessArea}
            reloadOrderAfterStatusChange={false}
            fullWidth={isMobile}
            scrollerContainerId={props.scrollerContainerId}
            position="fixed"
            hasEvaluationResult={props.app.reports.evaluationReportId != null}
            onEvaluationResultRequired={setPendingEvaluationStatusId}
        />
    );

    const notMobileRow = (
        <>
            <TableRow
                className={clsx(styles.row, { [styles.withoutBorder]: hasNotes }, props.className)}
                onClick={clickHandler}
            >
                <TableCell className={styles.clientNameCell}>
                    <PetParent
                        customer={props.app.customer}
                        marketplaceId={props.app.marketplaceId}
                        showOnly={!allowActions}
                        showTagsIcon={!isMobile}
                        onClick={props.onShowClient}
                    />
                </TableCell>
                <TableCell>{petInfo}</TableCell>
                <TableCell>{service}</TableCell>
                <TableCell>{props.app.startAt.format('MMM DD, YYYY [at] h:mm a')}</TableCell>
                <TableCell className={styles.priceCell}>
                    ${Number(props.app.total).toFixed(2)}
                    <DepositPendingIcon show={props.app.bookingDepositPending} />
                    <PaidIcon paid={props.app.paid} />
                </TableCell>
                <TableCell
                    width={180}
                    className={styles.statusCell}
                >
                    {statusButton}
                </TableCell>
                <TableCell
                    isActions
                    onClick={event => event.stopPropagation()}
                >
                    {allowActions && (
                        <ThreeDotsDropdown
                            options={options}
                            direction={props.isLast ? 'up' : 'down'}
                        />
                    )}
                </TableCell>
            </TableRow>
            {hasNotes && (
                <NotesTableRow
                    notes={props.app.notes}
                    colSpan={7}
                />
            )}
        </>
    );

    const mobileRow = (
        <TableRow
            className={clsx(styles.mobileRow, props.className)}
            onClick={clickHandler}
        >
            <TableCell className={styles.mobileCell}>
                <div className={styles.mobileHeader}>
                    <div className={styles.mobilePetContainer}>{petInfo}</div>
                    <div className={styles.mobileDateTimeContainer}>
                        <span className={styles.mobileDateTime}>{props.app.startAt.format('MMM DD, h:mm a')}</span>
                    </div>
                </div>
                <div className={styles.mobileBody}>{service}</div>
                <div className={styles.mobileFooter}>{statusButton}</div>
            </TableCell>
        </TableRow>
    );

    const evaluationResultsDrawer = (
        <EvaluationReportDrawer
            appointmentId={props.app.appointmentId}
            serviceId={props.app.service.id}
            petId={props.app.pet.id}
            defaultViewMode="edit"
            onClose={() => setPendingEvaluationStatusId(undefined)}
            onSave={() => {
                if (pendingEvaluationStatusId == null) return;
                const statusId = pendingEvaluationStatusId;
                setPendingEvaluationStatusId(undefined);
                dispatch(changeStatusAppointmentThunk(apiClientWrapper, props.app.appointmentId, statusId));
            }}
        />
    );

    return (
        <>
            {isMobile ? mobileRow : notMobileRow}
            {pendingEvaluationStatusId != null && evaluationResultsDrawer}
        </>
    );
};

export default EvaluationTableRow;
